import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { EditForm } from "./pricingGridUtils";

interface PricingEditPopoverProps {
  editForm: EditForm;
  anchor: { top: number; left: number };
  onUpdate: (form: EditForm) => void;
  onSave: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export default function PricingEditPopover({
  editForm,
  anchor,
  onUpdate,
  onSave,
  onDelete,
  onClose,
}: PricingEditPopoverProps) {
  return (
    <div
      className="fixed z-50 bg-popover border rounded-lg shadow-lg p-3 w-64 animate-in fade-in-0 zoom-in-95"
      style={{ top: anchor.top, left: anchor.left }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="space-y-2.5">
        <div>
          <Label className="text-xs">Namn</Label>
          <Input
            value={editForm.name}
            onChange={(e) => onUpdate({ ...editForm, name: e.target.value })}
            className="h-7 text-sm"
          />
        </div>
        <div>
          <Label className="text-xs">Pris (kr/h)</Label>
          <NumberInput
            value={editForm.price ?? ""}
            onValueChange={(v) => onUpdate({ ...editForm, price: v || 0 })}
            className="h-7 text-sm"
            autoFocus
          />
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <Label className="text-xs">Start</Label>
            <Input
              type="time"
              value={editForm.startTime}
              onChange={(e) => onUpdate({ ...editForm, startTime: e.target.value })}
              className="h-7 text-sm"
            />
          </div>
          <div className="flex-1">
            <Label className="text-xs">Slut</Label>
            <Input
              type="time"
              value={editForm.endTime}
              onChange={(e) => onUpdate({ ...editForm, endTime: e.target.value })}
              className="h-7 text-sm"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={editForm.isPeak}
            onCheckedChange={(v) => onUpdate({ ...editForm, isPeak: v })}
          />
          <Label className="text-xs">Peak-tid</Label>
        </div>
        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">Dagar</span>
          <div className="flex gap-1">
            {["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"].map((label, i) => {
              const dayNum = i + 1;
              return (
                <button
                  key={dayNum}
                  type="button"
                  className={cn(
                    "h-7 w-9 rounded text-xs font-medium transition-colors",
                    (editForm.days ?? []).includes(dayNum)
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  )}
                  onClick={() => {
                    const days = editForm.days ?? [];
                    const next = days.includes(dayNum)
                      ? days.filter((d) => d !== dayNum)
                      : [...days, dayNum].sort();
                    if (next.length > 0) onUpdate({ ...editForm, days: next });
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex gap-1.5 pt-1">
          <Button size="sm" className="flex-1 h-7 text-xs" onClick={onSave}>
            Spara
          </Button>
          <Button size="sm" variant="destructive" className="h-7 text-xs gap-1" onClick={onDelete}>
            <Trash2 className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onClose}>
            Avbryt
          </Button>
        </div>
      </div>
    </div>
  );
}
